import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { Group, Category, Rule, Txn, Budget } from '../money.model';

export interface CategoryRow { category: Category; rules: Rule[]; }
export interface GroupRow { group: Group; categories: CategoryRow[]; }

/** Pure — exported so it can be tested without Angular. */
export function groupCategories(groups: Group[], categories: Category[], rules: Rule[]): GroupRow[] {
  return [...groups]
    .sort((a, b) => a.order - b.order)
    .map(group => ({
      group,
      categories: categories
        .filter(c => c.groupId === group.id)
        .sort((a, b) => a.order - b.order)
        .map(category => ({
          category,
          rules: rules
            .filter(r => r.categoryId === category.id)
            .sort((a, b) => b.hitCount - a.hitCount),
        })),
    }));
}

/**
 * Pure — exported so the seed-latch decision can be tested without TestBed.
 * `seededAt === null` means never seeded; `seeding` guards the window between
 * kicking off the write and the durable marker landing back through the
 * subscription.
 *
 * Structural guarantee: this function keys ONLY on the durable seededAt
 * marker and deliberately never consults group counts. So a taxonomy that was
 * seeded but then deliberately emptied can never silently reseed. If this
 * function ever fell back to "groups is empty", it would resurrect the
 * fixed-id seed taxonomy over a user's deliberate clearing.
 */
export function shouldSeed(seededAt: number | null, seeding: boolean): boolean {
  return seededAt === null && !seeding;
}

/**
 * Every transaction referencing a category — transfers and excluded rows
 * included, because ANY row pointing at the id orphans when it is deleted.
 * This guard exists because it happened: 46 paychecks orphaned by a
 * delete-and-recreate, repaired by hand on 2026-09-01.
 */
export function txnsUsingCategory(txns: Txn[], categoryId: string): Txn[] {
  return (txns ?? []).filter(t => t.categoryId === categoryId);
}

/**
 * Every rule pointing at a category. Left orphaned, a rule silently
 * recreates the orphan on every future import — `applyRules` stamps
 * `categoryId: hit.categoryId` onto matching rows with no existence check.
 * This is how the 46-paycheck incident kept recurring after the transactions
 * were repaired by hand: the rule that caused it was never repointed.
 */
export function rulesUsingCategory(rules: Rule[], categoryId: string): Rule[] {
  return (rules ?? []).filter(r => r.categoryId === categoryId);
}

/**
 * Whether the category carries a budget node. Unlike transactions and rules
 * there is never more than one to find — a budget is keyed directly by
 * categoryId (`budgets/<categoryId>`) — so this returns a boolean rather
 * than a list.
 */
export function categoryHasBudget(budgets: Record<string, Budget>, categoryId: string): boolean {
  return !!(budgets ?? {})[categoryId];
}

/** "a, b and c" — never an Oxford-comma-less run-on when there are 3 parts. */
function joinParts(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return parts.join(' and ');
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * Pure — exported so the copy can be tested without Angular. Counts
 * transactions, rules, and budgets SEPARATELY rather than folding them into
 * one number: each is reassigned (or cleared) through a different write,
 * and conflating them would hide that one of the three is affected at all.
 */
export function pendingDeleteMessage(
  name: string, txnCount: number, ruleCount: number, budgetCount = 0,
): string {
  const parts: string[] = [];
  if (txnCount > 0) parts.push(`${txnCount} transaction${txnCount === 1 ? '' : 's'}`);
  if (ruleCount > 0) parts.push(`${ruleCount} rule${ruleCount === 1 ? '' : 's'}`);
  if (budgetCount > 0) parts.push(`${budgetCount} budget${budgetCount === 1 ? '' : 's'}`);
  const verb = (txnCount + ruleCount + budgetCount) === 1 ? 'uses' : 'use';
  return `${joinParts(parts)} ${verb} ${name}`;
}

@Component({
  selector: 'app-money-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
  standalone: false,
})
export class CategoriesComponent implements OnInit, OnDestroy {
  rows: GroupRow[] = [];
  loading = true;
  uid = '';
  newNames: Record<string, string> = {};
  error = '';

  /** Non-null while a referenced category's delete awaits a reassignment target. */
  pendingDelete:
    {
      categoryId: string; name: string;
      txnCount: number; ruleCount: number; hasBudget: boolean; toId: string;
    }
    | null = null;

  private subs: Subscription[] = [];
  private seeding = false;
  private txns: Txn[] = [];
  private rules: Rule[] = [];
  private budgets: Record<string, Budget> = {};

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      this.subs.push(this.money.subscribeAll(this.uid).subscribe(async data => {
        // Latched on a durable marker, NOT on "groups is empty". Inferring
        // never-seeded from currently-empty would silently resurrect the seed
        // taxonomy — with its fixed ids — for anyone who deliberately cleared
        // it. `seeding` guards the window before the write lands.
        if (shouldSeed(data.seededAt, this.seeding)) {
          this.seeding = true;
          try { await this.money.seedTaxonomy(this.uid); }
          finally { this.seeding = false; }
          return;
        }
        if (data.seededAt === null) return;   // seed in flight

        this.txns = data.txns;
        this.rules = data.rules;
        this.budgets = data.budgets;
        this.rows = groupCategories(data.groups, data.categories, data.rules);
        this.loading = false;
      }));
    }));
  }

  // BOTH subscriptions: an unstored authState subscription outlives the
  // component and re-runs against a destroyed instance on the next auth event.
  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  trackGroup(_: number, row: GroupRow): string { return row.group.id; }
  trackCategory(_: number, row: CategoryRow): string { return row.category.id; }
  trackRule(_: number, rule: Rule): string { return rule.id; }

  get guardMessage(): string {
    const p = this.pendingDelete;
    return p ? pendingDeleteMessage(p.name, p.txnCount, p.ruleCount, p.hasBudget ? 1 : 0) : '';
  }

  /**
   * Every template-invoked async handler routes through this so a rejected
   * write surfaces as a visible message instead of an unhandled rejection the
   * user never sees.
   */
  private async safe(action: () => Promise<void>): Promise<void> {
    this.error = '';
    try {
      await action();
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  async addCategory(groupId: string): Promise<void> {
    const name = (this.newNames[groupId] ?? '').trim();
    if (!name) return;
    await this.safe(async () => {
      const row = this.rows.find(r => r.group.id === groupId);
      const order = row ? row.categories.length : 0;
      await this.money.addCategory(this.uid, { name, groupId, order });
      this.newNames[groupId] = '';
    });
  }

  async removeCategory(id: string): Promise<void> {
    const txnRefs = txnsUsingCategory(this.txns, id);
    const ruleRefs = rulesUsingCategory(this.rules, id);
    const hasBudget = categoryHasBudget(this.budgets, id);
    if (txnRefs.length === 0 && ruleRefs.length === 0 && !hasBudget) {
      await this.safe(() => this.money.deleteCategory(this.uid, id));
      return;
    }
    const name = this.rows
      .reduce<CategoryRow[]>((acc, row) => acc.concat(row.categories), [])
      .find(cr => cr.category.id === id)?.category.name ?? 'this category';
    this.pendingDelete = {
      categoryId: id, name, txnCount: txnRefs.length, ruleCount: ruleRefs.length, hasBudget, toId: '',
    };
  }

  cancelDelete(): void { this.pendingDelete = null; }

  /**
   * Reassign FIRST, delete second. A failure between the writes leaves the
   * category present with rows already moved — retrying just deletes.
   * Delete-first would recreate the orphan incident this guard exists for.
   *
   * Reassigns BOTH transactions and rules to the chosen target. Leaving
   * rules behind is exactly how the original incident kept recurring:
   * applyRules stamps a rule's categoryId onto every future import match,
   * so an orphaned rule silently regenerates orphaned transactions forever.
   */
  async confirmDelete(): Promise<void> {
    const p = this.pendingDelete;
    if (!p || !p.toId || p.toId === p.categoryId) return;

    // The target may have been deleted in another tab while this picker was
    // open — ngModel does not clear `toId` when its <option> disappears.
    // Writing a dead id here would recreate the orphan incident through a
    // different door, so refuse and make the user pick again.
    const targetStillExists = this.rows
      .some(row => row.categories.some(cr => cr.category.id === p.toId));
    if (!targetStillExists) {
      this.pendingDelete = { ...p, toId: '' };
      this.error = 'That target category no longer exists. Pick another.';
      return;
    }

    await this.safe(async () => {
      const patches: Record<string, Partial<Omit<Txn, 'id'>>> = {};
      txnsUsingCategory(this.txns, p.categoryId)
        .forEach(t => { patches[t.id] = { categoryId: p.toId }; });
      const ruleWrites = rulesUsingCategory(this.rules, p.categoryId)
        .map(r => this.money.updateRule(this.uid, r.id, { categoryId: p.toId }));
      await Promise.all([this.money.updateTxns(this.uid, patches), ...ruleWrites]);

      // The budget node is keyed by categoryId, so it does not follow a
      // reassignment on its own — left alone it becomes an orphan, later
      // rendering as a live-limit "Unknown" row that groupBudgetRollup
      // silently drops (Finding 2). Move the default limit onto the target
      // when it has none of its own — never clobbering a limit the target
      // already set — otherwise the old budget is simply cleared. Overrides
      // are not carried over: only the default amount is a "move", per the
      // fix scope.
      const oldBudget = this.budgets[p.categoryId];
      if (oldBudget) {
        const targetHasBudget = !!this.budgets[p.toId];
        if (!targetHasBudget && oldBudget.amount !== undefined && oldBudget.amount !== null) {
          await this.money.setBudget(this.uid, p.toId, oldBudget.amount);
        }
        await this.money.setBudget(this.uid, p.categoryId, null);
      }

      await this.money.deleteCategory(this.uid, p.categoryId);
      this.pendingDelete = null;
    });
  }

  async removeRule(id: string): Promise<void> {
    await this.safe(() => this.money.deleteRule(this.uid, id));
  }
}
